import { useCallback, useEffect, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AvatarIniciales, KpiCard, KpiGrid, NoSeBorra, ZonaPeligro } from "@/components/ds";
import { ArrowLeft, ChevronDown, Download, Loader2, Save } from "@/components/ds/icons";
import { api, ApiError } from "@/lib/api";
import { formatRut } from "@/lib/rut";
import { formatNombreArea, ordenarAreas } from "@/lib/area";
import { TrabajadorForm } from "@/pages/personas/TrabajadorForm";
import { DocumentacionGeneralPanel } from "@/components/persona-documentos/DocumentacionGeneralPanel";
import { CoberturaCapacitaciones } from "@/components/personas/CoberturaCapacitaciones";
import { BandejaEntrada } from "@/pages/documentos/BandejaEntrada";
import type { Trabajador } from "@/types/trabajador";
import type {
  TrabajadorResumen,
  TrabajadoresResumenResponse,
} from "@/types/trabajador-resumen";

/**
 * Ficha del trabajador — detalle con encabezado de identidad, tres KPI de
 * cumplimiento y cuatro pestañas (Información, Documentación General,
 * Documentos Terreno, Capacitaciones).
 *
 * La Ficha es el ÚNICO lugar donde se edita un trabajador y donde se lo mueve a
 * la papelera: el listado quedó de sólo lectura a propósito, para que no haya
 * dos caminos que mantener con el mismo texto legal.
 *
 * Los tres KPI salen del MISMO `GET /personas/resumen` que alimenta las
 * columnas del listado. Reusarlo en vez de crear un endpoint por persona
 * garantiza que la ficha no pueda contradecir a la fila de la que se llegó —
 * si el número de la tabla y el de la ficha salen de dos caminos distintos,
 * tarde o temprano difieren.
 */

/** Perfiles principales del dropdown de descarga — ver `?perfil=` en fc-api. */
const PERFILES_PRINCIPALES: Array<{ label: string; perfil: string }> = [
  { label: "Toda la documentación", perfil: "todo" },
  { label: "Documentación General", perfil: "general" },
  { label: "Documentación de Terreno", perfil: "terreno" },
];

/** Perfiles de Terreno filtrados por tipo — subconjunto de `PERFILES_PRINCIPALES`. */
const PERFILES_TERRENO_POR_TIPO: Array<{ label: string; perfil: string }> = [
  { label: "EPP", perfil: "terreno_epp" },
  { label: "ATS", perfil: "terreno_ats" },
  { label: "Capacitaciones", perfil: "terreno_capacitacion" },
  { label: "Check Vehículos", perfil: "terreno_check_camioneta" },
  { label: "Check Maquinaria", perfil: "terreno_check_maquinaria" },
];

function formatFechaCorta(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function plural(n: number, singular: string, plurales: string): string {
  return `${n} ${n === 1 ? singular : plurales}`;
}

export function FichaTrabajador() {
  const [, params] = useRoute("/personas/trabajadores/:uuid/ficha");
  const [, navigate] = useLocation();
  const uuid = params?.uuid;

  const [persona, setPersona] = useState<Trabajador | null>(null);
  const [resumen, setResumen] = useState<TrabajadorResumen | null>(null);
  // El `meta` viaja aparte del resumen por trabajador porque describe a la
  // EMPRESA (cuántos tipos exige, con cuántos días avisa), no a la persona.
  const [metaResumen, setMetaResumen] = useState<TrabajadoresResumenResponse["meta"] | null>(null);
  const [dotacion, setDotacion] = useState<number | undefined>(undefined);
  // Ventana de "por vencer" que usa el SERVIDOR (`max(VentanasAviso::OPCIONES)`
  // = 60 días, la anticipación con la que puede mandar el correo). El cliente
  // tiene su propio default de 30 en `lib/vencimiento.ts`, y con los dos en la
  // misma pantalla el KPI decía "2 por vencer" mientras ninguna fila del tab
  // aparecía marcada. El tab mide con este número, no con el default.
  const [diasPorVencer, setDiasPorVencer] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [descargandoZip, setDescargandoZip] = useState(false);
  const [aEliminar, setAEliminar] = useState(false);
  const [eliminando, setEliminando] = useState(false);

  // `GET /personas/{persona}` bindea por `id` autoincremental (no hay
  // `getRouteKeyName` override en el modelo `Persona`), y no existe un
  // endpoint "por uuid". Para no tocar fc-api en este slice, buscamos el
  // trabajador dentro del listado — que YA está scoped a la empresa actual
  // vía `EmpresaContext::require()`. Esto además resuelve el guard
  // multi-tenant "gratis": un uuid de otra empresa simplemente no aparece en
  // la respuesta y cae en el estado "no encontrado".
  const cargar = useCallback(async () => {
    if (!uuid) return;
    setIsLoading(true);
    setNotFound(false);
    try {
      const res = await api.get<{ data: Trabajador[] }>("/personas?all=1");
      // La ficha ya paga el listado completo para resolver el uuid; de paso
      // sale la dotación, que el modal de exigibles necesita para decir a
      // cuánta gente alcanza el cambio.
      setDotacion(res.data.length);
      const encontrado = res.data.find((p) => p.uuid === uuid) ?? null;
      if (!encontrado) {
        setNotFound(true);
        setPersona(null);
      } else {
        setPersona(encontrado);
      }
    } catch {
      setNotFound(true);
      setPersona(null);
    } finally {
      setIsLoading(false);
    }
  }, [uuid]);

  // Los KPI son secundarios: si el resumen falla, la ficha sigue siendo
  // editable y sólo las tres tarjetas caen a "—". Por eso no toca `notFound`.
  const cargarResumen = useCallback(async () => {
    if (!uuid) return;
    try {
      const res = await api.get<TrabajadoresResumenResponse>("/personas/resumen");
      setResumen(res.data.find((r) => r.persona_uuid === uuid) ?? null);
      setDiasPorVencer(res.meta.dias_por_vencer);
      setMetaResumen(res.meta);
    } catch {
      setResumen(null);
    }
  }, [uuid]);

  useEffect(() => {
    cargar();
    cargarResumen();
  }, [cargar, cargarResumen]);

  // Descarga un subconjunto de documentos del trabajador (según `perfil`) en
  // un único ZIP streameado. Mismo patrón blob → object URL → <a> sintético
  // ya usado por `descargarPdf` (BandejaEntrada.tsx) y `Empresa.tsx` — el
  // revoke se difiere con setTimeout(0) para no cancelar la descarga en
  // navegadores que tratan el click de `<a download>` como asíncrono.
  //
  // El nombre del ZIP lo decide fc-api por perfil (Content-Disposition) —
  // acá solo se usa como fallback si el header no llegó.
  async function descargarZip(perfil: string) {
    if (!persona || descargandoZip) return;
    setDescargandoZip(true);
    try {
      const { blob, filename } = await api.blobWithFilename(
        `/personas/${persona.uuid}/documentos/zip?perfil=${perfil}`
      );
      const rut = persona.rut.replace(/[^0-9Kk]/g, "");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename ?? `${perfil}_${rut}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "No se pudo descargar el ZIP.";
      toast.error(msg);
    } finally {
      setDescargandoZip(false);
    }
  }

  // Mover a la papelera vive SÓLO acá (antes estaba también en la fila del
  // listado). Al confirmar se vuelve al listado: la ficha de alguien que ya no
  // está en la dotación no tiene nada que mostrar.
  async function moverAPapelera() {
    if (!persona) return;
    setEliminando(true);
    try {
      await api.delete(`/personas/${persona.id}`);
      toast.success(
        `Trabajador "${persona.nombre} ${persona.apellido}" movido a la papelera. Su acceso queda suspendido.`
      );
      setAEliminar(false);
      navigate("/personas/trabajadores");
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error("Este trabajador ya está en la papelera.");
      } else {
        toast.error("No se pudo mover el trabajador a la papelera.");
      }
      setEliminando(false);
    }
  }

  if (!uuid) return null;

  const nombreCompleto = persona ? `${persona.nombre} ${persona.apellido}` : "";
  const areasTexto =
    persona && persona.areas.length > 0
      ? ordenarAreas(persona.areas).map(formatNombreArea).join(" · ")
      : "Sin área";

  // ── KPI ────────────────────────────────────────────────────────────────────
  // DENOMINADOR = los tipos que la empresa declaró exigir, no el catálogo. El
  // catálogo entero sigue siendo una lista de REFERENCIA y medir contra los 48
  // convertiría un checklist opcional en 48 incumplimientos aparentes.
  //
  // Mientras la empresa no configuró nada (`exigibles_configurados === false`)
  // no hay denominador legítimo y la tarjeta no muestra fracción: dice cuántos
  // documentos hay y manda a configurar. Es el MISMO criterio que la tarjeta de
  // progreso del tab y que la columna del listado — las tres salen de
  // `/personas/resumen`, así que no pueden contradecirse.
  const doc = resumen?.documentacion;
  const exigibles = metaResumen?.exigibles ?? 0;
  const exigiblesConfigurados = metaResumen?.exigibles_configurados ?? false;
  const cap = resumen?.capacitaciones;
  const ter = resumen?.terreno;

  return (
    <div className="space-y-5">
      <button
        type="button"
        onClick={() => navigate("/personas/trabajadores")}
        className="text-muted-foreground hover:text-foreground flex w-fit items-center gap-1.5 text-[12.5px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver a Trabajadores
      </button>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12 pt-6">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </CardContent>
        </Card>
      ) : notFound || !persona ? (
        <Card>
          <CardContent className="text-muted-foreground py-12 text-center text-sm">
            No se encontró el trabajador solicitado, o no pertenece a tu empresa.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Encabezado de identidad */}
          <div className="flex flex-wrap items-start gap-3.5">
            <AvatarIniciales iniciales={persona.iniciales} tamano="ficha" />
            <div className="min-w-[220px] flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-2xl font-bold tracking-[-.025em]">{nombreCompleto}</h1>
                <Badge variant={persona.activo ? "success" : "neutral"}>
                  {persona.activo ? "Activo" : "Inactivo"}
                </Badge>
              </div>
              <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-2 text-[12.5px]">
                <span className="fc-num">{formatRut(persona.rut)}</span>
                <span className="text-border-strong">·</span>
                <span>{persona.cargo?.nombre ?? "Sin cargo"}</span>
                <span className="text-border-strong">·</span>
                <span>{areasTexto}</span>
                <span className="text-border-strong">·</span>
                <span>{persona.tiene_licencia ? "Con app móvil" : "Sin app móvil"}</span>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button disabled={descargandoZip} className="shrink-0">
                  {descargandoZip ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Descargar carpeta
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {PERFILES_PRINCIPALES.map(({ label, perfil }) => (
                  <DropdownMenuItem key={perfil} onSelect={() => descargarZip(perfil)}>
                    {label}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Terreno por tipo</DropdownMenuLabel>
                {PERFILES_TERRENO_POR_TIPO.map(({ label, perfil }) => (
                  <DropdownMenuItem key={perfil} onSelect={() => descargarZip(perfil)}>
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <KpiGrid min={190}>
            <KpiCard
              label="Documentación"
              valor={
                !doc || !exigiblesConfigurados || exigibles === 0
                  ? (doc?.cargados ?? "—")
                  : `${doc.exigibles_cargados}/${exigibles}`
              }
              alerta={Boolean(doc && doc.vencidos > 0)}
              detalle={
                !doc
                  ? "sin datos de cumplimiento"
                  : !exigiblesConfigurados
                    ? "documentos cargados · falta definir cuáles exige tu empresa"
                    : exigibles === 0
                      ? "documentos cargados · tu empresa no exige ninguno"
                      : doc.vencidos > 0
                        ? `${plural(doc.vencidos, "vencido", "vencidos")} de ${exigibles} exigibles`
                        : doc.exigibles_cargados < exigibles
                          ? `${exigibles - doc.exigibles_cargados} sin subir de ${exigibles} exigibles`
                          : doc.por_vencer > 0
                            ? `${doc.por_vencer} por vencer · ${exigibles} exigibles al día`
                            : `documentos exigibles cargados`
              }
            />
            <KpiCard
              label="Capacitaciones"
              valor={cap ? `${cap.completas}/${cap.total}` : "—"}
              detalle={
                !cap
                  ? "sin datos de cumplimiento"
                  : cap.total === 0
                    ? "todavía no le llegó ninguna"
                    : cap.pendientes > 0
                      ? `${plural(cap.pendientes, "pendiente", "pendientes")} de cerrar`
                      : "todas al día"
              }
            />
            <KpiCard
              label="Firmas en terreno"
              valor={ter ? ter.total : "—"}
              detalle={
                ter?.ultima_firma_at
                  ? `última ${formatFechaCorta(ter.ultima_firma_at)}`
                  : "sin registros firmados en la app"
              }
            />
          </KpiGrid>

          <Tabs defaultValue="info" variant="ficha" className="space-y-4">
            {/* `flex-wrap max-w-full`: las cuatro pestañas suman ~538px y el
                riel del segmented es `w-fit`, así que abajo de ~540px de
                viewport desbordaba y hacía scrollear la PÁGINA entera de
                costado (medido: scrollWidth 538 con innerWidth 500). El
                prototipo lo resuelve igual (`flex-wrap:wrap;max-width:100%`).
                Va en el punto de uso y no en `segmented.ts` para no cambiarle
                el comportamiento a los segmented del resto de la app. */}
            <TabsList className="max-w-full flex-wrap">
              <TabsTrigger value="info">Información</TabsTrigger>
              <TabsTrigger value="adicionales">Documentación General</TabsTrigger>
              <TabsTrigger value="terreno">Documentos Terreno</TabsTrigger>
              <TabsTrigger value="capacitaciones">Capacitaciones</TabsTrigger>
            </TabsList>

            <TabsContent value="info" className="space-y-3">
              {/* Sin Card envolvente: `layout="secciones"` ya trae sus propias
                  tarjetas, y anidarlas daría un borde dentro de otro borde. */}
              <TrabajadorForm
                mode="edit"
                trabajador={persona}
                autoFocusFirst={false}
                layout="secciones"
                formClassName="space-y-3"
                onSaved={(actualizado) => {
                  setPersona(actualizado);
                  cargarResumen();
                }}
                renderFooter={({ isSubmitting }) => (
                  <div className="flex justify-end">
                    <Button type="submit" disabled={isSubmitting} className="gap-2">
                      {isSubmitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      Guardar cambios
                    </Button>
                  </div>
                )}
              />

              {/* La zona de peligro va fuera del <form> — ver ZonaPeligro. */}
              <ZonaPeligro
                titulo="Mover a la papelera"
                descripcion={
                  <>
                    {nombreCompleto} sale de la dotación y de los envíos, pero <NoSeBorra />: sus
                    documentos, firmas en terreno y resultados quedan guardados. Puedes
                    recuperarlo desde Trabajadores › Papelera, o eliminarlo definitivamente desde
                    ahí.
                  </>
                }
                accion="Mover a la papelera"
                onAccion={() => setAEliminar(true)}
              />
            </TabsContent>

            {/* Sin encabezado propio: el título de cada panel repetía palabra por
                palabra la etiqueta de su pestaña, que está a treinta píxeles de
                distancia y ya está seleccionada. */}
            <TabsContent value="adicionales" className="space-y-4">
              <DocumentacionGeneralPanel
                personaUuid={persona.uuid}
                personaEmail={persona.email}
                diasPorVencer={diasPorVencer}
                onDocumentosChange={cargarResumen}
                dotacion={dotacion}
              />
            </TabsContent>

            <TabsContent value="terreno" className="space-y-4">
              <BandejaEntrada lockedPersonaUuid={persona.uuid} />
            </TabsContent>

            <TabsContent value="capacitaciones" className="space-y-4">
              <CoberturaCapacitaciones personaUuid={persona.uuid} />
            </TabsContent>
          </Tabs>

          <AlertDialog open={aEliminar} onOpenChange={(open) => !open && setAEliminar(false)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Mover a papelera</AlertDialogTitle>
                <AlertDialogDescription>
                  ¿Quieres mover a <strong>{nombreCompleto}</strong> a la papelera? Sale de la
                  dotación y de los envíos, pero <strong>no se borra</strong>: sus documentos,
                  firmas en terreno y resultados quedan guardados. Puedes recuperarlo desde
                  Trabajadores › Papelera. Nota: esto es distinto de inactivar — inactivar solo lo
                  saca de las asignaciones operativas sin suspender su acceso.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={eliminando}>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    moverAPapelera();
                  }}
                  disabled={eliminando}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {eliminando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mover a papelera"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}
